/** Polling met resume-gedrag op één plek, omdat elke tab dezelfde drie
 *  fouten kopieerde (2026-08-12): een fout die nooit meer gewist werd,
 *  doorpollen op een verborgen scherm, en alarm bij één hikje. Daarom:
 *  fout pas na TOLERATED_FAILURES opeenvolgende mislukkingen (of
 *  meteen als er nog nooit data was), wissen zodra iets lukt, pauzeren
 *  bij verborgen document, direct verversen bij terugkomst of netwerk.
 *  docs/customer-app.md > Front-end > Polling with resume
 */

import { useCallback, useEffect, useRef, useState } from "react";

export const TOLERATED_FAILURES = 2;

export function usePoll<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
): { data: T | null; error: string | null; refresh: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Refs: de poll-lus mag niet opnieuw opgezet worden bij elke render.
  const seq = useRef(0);
  const fails = useRef(0);
  const hasData = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async () => {
    const my = ++seq.current;
    try {
      const v = await fetcherRef.current();
      if (my !== seq.current) return;   // een nieuwere run won
      fails.current = 0;
      hasData.current = true;
      setData(v);
      setError(null);
    } catch (e) {
      if (my !== seq.current) return;
      fails.current += 1;
      if (fails.current >= TOLERATED_FAILURES || !hasData.current) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, []);

  useEffect(() => {
    let stopped = false;
    const tick = () => {
      // Verborgen tab/app: niets doen. De radio slaapt en het antwoord
      // zou toch niemand bereiken.
      if (document.visibilityState === "hidden" || stopped) return;
      void run();
    };
    void run();
    const id = window.setInterval(tick, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") void run();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      stopped = true;
      seq.current++;                    // laat late antwoorden vallen
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
    };
  }, [intervalMs, run]);

  return { data, error, refresh: run };
}
